/* eslint-disable react/prop-types */
/**
 * Small react-leaflet helper components + icon factory shared by every map in
 * the app. These replace the imperative `window.google.maps.*` plumbing
 * (click listeners, fitBounds, recenters, custom marker icons).
 */
import { useEffect, useRef } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";

/** Build a Leaflet icon from an imported SVG/PNG asset url. */
export function makeIcon(url, width = 40, height = 40, anchor) {
  return L.icon({
    iconUrl: url,
    iconSize: [width, height],
    iconAnchor: anchor || [width / 2, height],
    popupAnchor: [0, -height + 8],
  });
}

/** Build a Leaflet divIcon from raw HTML (used for pulses / dots). */
export function makeDivIcon(html, size = [24, 24], anchor) {
  return L.divIcon({
    className: "",
    html,
    iconSize: size,
    iconAnchor: anchor || [size[0] / 2, size[1] / 2],
  });
}

// A click whose target sits inside a map control OR any form field / link
// (the search box + its result buttons, the layer toggle, the locate button).
// These intentionally don't stop propagation, so a click on them otherwise
// bubbles up as a map click. This mirrors the Survey map's own `onControl`
// guard, and it's what keeps picking a place from search from ALSO registering
// as a map click, on a zoom-gated map (`zoomOnTooFar`) that stray click would
// otherwise fly to the gate zoom and look like the search itself zoomed.
const onControl = (t) =>
  !!t &&
  !!t.closest &&
  !!(
    t.closest(".leaflet-control") ||
    t.closest("input") ||
    t.closest("button") ||
    t.closest("a")
  );

/** Fires `onClick(lat, lng)` on map click unless `disabled`. */
export function ClickHandler({ onClick, disabled, minZoom, onTooFar, zoomOnTooFar, onAnyClick, pickOnTooFar }) {
  const map = useMapEvents({
    click(e) {
      if (disabled || !onClick) return;
      // Ignore clicks that land on a control / search box / result button (see
      // `onControl`). This is the deterministic guard Survey uses.
      if (onControl(e.originalEvent?.target)) return;
      // Belt-and-suspenders for the ONE case `onControl` can miss: selecting a
      // search result removes its button on mousedown, so the browser can
      // dispatch the trailing `click` on the map container itself (target no
      // longer inside a control). `SearchUI.select` stamps the time so we can
      // swallow exactly that leaked click. The window is generous (1500 ms)
      // because the search `panTo` animation delays the trailing click; the
      // stamp is consumed once used so it never eats a genuine later click.
      if (map._jaagaSearchSelectAt && Date.now() - map._jaagaSearchSelectAt < 1500) {
        map._jaagaSearchSelectAt = 0;
        return;
      }
      // A genuine map click (control / search-leak clicks already filtered out
      // above), fired BEFORE the zoom gate so callers hear "the user clicked the
      // map" even when the click only zooms in instead of placing a point.
      onAnyClick?.(e.latlng.lat, e.latlng.lng);
      // Below the gate zoom (~20 m), a click does NOT place a point (the
      // location stays precise). With `zoomOnTooFar` the click instead flies the
      // map into that exact spot at the gate zoom, so the next click places it;
      // otherwise we just prompt the user to zoom in.
      if (minZoom != null && map.getZoom() < minZoom) {
        if (zoomOnTooFar) map.flyTo(e.latlng, minZoom);
        else onTooFar?.();
        // `pickOnTooFar` places the point on this click anyway (while still
        // flying in). Without it the first click on a zoomed-out map does
        // nothing visible, which reads as "clicking doesn't work" — the point is
        // what the user is asking for, and they can drag or re-click to refine
        // once the fly-in lands.
        if (!pickOnTooFar) return;
      }
      onClick(e.latlng.lat, e.latlng.lng, { openModal: true });
    },
  });
  return null;
}

/** Reports the live zoom level to `onZoom` (initial + on every zoom change). */
export function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({
    zoomend: () => onZoom(map.getZoom()),
  });
  useEffect(() => {
    onZoom(map.getZoom());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/** Smoothly recenters (and optionally re-zooms) when `center` changes. */
export function Recenter({ center, zoom, fly = false }) {
  const map = useMap();
  const lat = center?.lat;
  const lng = center?.lng;
  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const z = Number.isFinite(zoom) ? zoom : map.getZoom();
    if (fly) map.flyTo([lat, lng], z);
    else map.setView([lat, lng], z);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, zoom]);
  return null;
}

/**
 * Smoothly flies to `point` ({ lat, lng }) whenever it changes, used for
 * hover-to-locate from a list (mirrors the My Property hover-fly). Debounced so
 * sweeping the cursor across a list doesn't spam flyTo.
 */
export function FlyTo({ point, zoom = 15, delay = 200 }) {
  const map = useMap();
  const lat = point?.lat;
  const lng = point?.lng;
  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
    // Debounced (resets on each change) so scrolling a list, which fires a burst
    // of hover events as rows pass under the cursor, coalesces to a single fly.
    const t = setTimeout(() => {
      // Skip if we're already essentially there (avoids a redundant re-animation
      // when the same row is re-detected).
      const c = map.getCenter();
      if (Math.abs(c.lat - lat) < 1e-5 && Math.abs(c.lng - lng) < 1e-5) return;
      map.flyTo([lat, lng], Math.max(map.getZoom(), zoom), { duration: 0.6 });
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);
  return null;
}

/** Fits the map to a set of `{ lat, lng }` points (the OSM `fitBounds`). */
export function FitBounds({ points, padding = [40, 40], maxZoom = 17 }) {
  const map = useMap();
  const key = (points || [])
    .map((p) => `${p?.lat},${p?.lng}`)
    .join("|");
  useEffect(() => {
    const pts = (points || [])
      .filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng))
      .map((p) => [p.lat, p.lng]);
    if (pts.length > 1) {
      map.fitBounds(L.latLngBounds(pts), { padding, maxZoom });
    } else if (pts.length === 1) {
      map.setView(pts[0], Math.max(map.getZoom(), 14));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
}

/**
 * Forces a `invalidateSize()` shortly after mount. Essential when a map lives
 * inside a MUI Popover / Modal / tab where the container has zero size on the
 * first paint and tiles would otherwise render as grey boxes.
 */
export function InvalidateOnMount({ delay = 200 }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/** Captures the Leaflet map instance into a parent setter/ref. */
export function MapReady({ onReady }) {
  const map = useMap();
  const called = useRef(false);
  useEffect(() => {
    if (called.current) return;
    called.current = true;
    onReady?.(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
}
